import { useEffect, useRef } from 'react';
import './ContactHeader.css';

const ContactHeader = () => {
  const canvasRef = useRef(null);
  const textRef = useRef(null);
  const mousePos = useRef({ x: 0, y: 0 });
  const targetPos = useRef({ x: 0, y: 0 });
  const animationFrameId = useRef(null);
  const containerRef = useRef(null);
  const isHovering = useRef(false);
  const textDimensions = useRef({ width: 0, height: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const textElement = textRef.current;
    const container = containerRef.current;
    if (!canvas || !textElement || !container) return;

    const ctx = canvas.getContext('2d');
    let dots = [];

    // Set canvas size
    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      
      // Regenerate dots on resize
      generateDots();

      // Update text dimensions - measure actual rendered text width
      if (textElement) {
        // Force layout calculation to get accurate dimensions
        const computedStyle = window.getComputedStyle(textElement);
        const fontSize = computedStyle.fontSize;
        
        // Create temporary canvas to measure text
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.font = `700 ${fontSize} ${computedStyle.fontFamily}`;
        const metrics = tempCtx.measureText(textElement.textContent);
        
        textDimensions.current = {
          width: metrics.width,
          height: parseFloat(fontSize)
        };
      }

      // Set default position to left
      const defaultX = rect.width * 0.15; // 15% from left
      const defaultY = rect.height / 2; // Vertically centered
      if (!isHovering.current) {
        mousePos.current = { x: defaultX, y: defaultY };
        targetPos.current = { x: defaultX, y: defaultY };
      }
    };

    // Generate dot grid
    const generateDots = () => {
      dots = [];
      const rect = container.getBoundingClientRect();
      const spacing = 40; // Space between dots
      // Add extra columns and rows to extend beyond visible area
      const cols = Math.ceil(rect.width / spacing) + 2; // +2 extra columns
      const rows = Math.ceil(rect.height / spacing) + 2; // +2 extra rows

      // Start from negative offset to extend left and top
      for (let i = -1; i < cols; i++) {
        for (let j = -1; j < rows; j++) {
          dots.push({
            x: i * spacing + spacing / 2,
            y: j * spacing + spacing / 2,
            baseSize: 3, // Base dot size
            currentSize: 3,
            targetSize: 3
          });
        }
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Mouse move handler
    const handleMouseMove = (e) => {
      isHovering.current = true;
      const rect = container.getBoundingClientRect();
      targetPos.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };

    // Mouse leave handler - reset dots and center text
    const handleMouseLeave = () => {
      isHovering.current = false;
      dots.forEach(dot => {
        dot.targetSize = dot.baseSize;
      });
      
      // Reset target position to default (left side)
      if (container) {
        const rect = container.getBoundingClientRect();
        targetPos.current = {
          x: rect.width * 0.15, // 15% from left edge
          y: rect.height / 2
        };
      }
    };

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('mouseenter', () => isHovering.current = true);

    // Animation loop
    const animate = () => {
      const rect = container.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      // Smooth mouse position with easing
      // Use slower lerp when returning to default position (not hovering)
      const lerpFactor = isHovering.current ? 0.15 : 0.03; // 0.03 for very slow, elegant return
      mousePos.current.x += (targetPos.current.x - mousePos.current.x) * lerpFactor;
      mousePos.current.y += (targetPos.current.y - mousePos.current.y) * lerpFactor;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Update and draw dots with proximity effect
      const proximityRadius = 120; // Radius of influence
      const maxScale = 5; // Maximum scale multiplier (reduced from 8 to 5)

      dots.forEach(dot => {
        // Only calculate proximity if hovering
        if (isHovering.current) {
          // Calculate distance from mouse to dot
          const dx = mousePos.current.x - dot.x;
          const dy = mousePos.current.y - dot.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // Calculate proximity influence (0 to 1)
          if (distance < proximityRadius) {
            const influence = 1 - (distance / proximityRadius);
            // Smooth easing curve for natural falloff
            const easedInfluence = influence * influence * (3 - 2 * influence);
            dot.targetSize = dot.baseSize + (dot.baseSize * maxScale * easedInfluence);
          } else {
            dot.targetSize = dot.baseSize;
          }
        } else {
          dot.targetSize = dot.baseSize;
        }

        // Smooth size transition
        dot.currentSize += (dot.targetSize - dot.currentSize) * 0.2;

        // Draw dot
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, dot.currentSize, 0, Math.PI * 2);
        
        // Color intensity: BLACK in center (near mouse), LIGHTER outside
        const sizeRatio = dot.currentSize / dot.baseSize;
        // When dot is larger (near cursor), it's BLACK. When smaller (far), it's light gray
        const opacity = sizeRatio > 1 
          ? 0.15 + (sizeRatio - 1) * 0.15 // Gets darker as it grows (0.15 -> 0.8+)
          : 0.15; // Light gray at base size
        
        ctx.fillStyle = `rgba(17, 17, 17, ${opacity})`;
        ctx.fill();
      });

      // ============================================================
      // MAGNETIC TEXT EFFECT - PRECISE BOUNDARY MAPPING
      // ============================================================
      // Goal: Mouse left (0%) = Text left, Mouse right (100%) = Text right
      
      // Get text dimensions dynamically in each frame for accuracy
      const textRect = textElement.getBoundingClientRect();
      const textWidth = textRect.width;
      const textHeight = textRect.height;
      const padding = 40; // Safety padding from edges
      
      // Calculate the boundaries where text can be positioned
      // Text position is measured from its LEFT edge
      const minTextX = padding; // Leftmost position (text's left edge at padding)
      const maxTextX = width - textWidth - padding; // Rightmost position
      const minTextY = padding;
      const maxTextY = height - textHeight - padding;
      
      // Get mouse position as ratio (0 to 1)
      const mouseXRatio = Math.max(0, Math.min(1, mousePos.current.x / width));
      const mouseYRatio = Math.max(0, Math.min(1, mousePos.current.y / height));
      
      // Map mouse ratio to absolute text position
      // When mouse is at 0% (left), text should be at minTextX (left boundary)
      // When mouse is at 100% (right), text should be at maxTextX (right boundary)
      const targetTextX = minTextX + (mouseXRatio * (maxTextX - minTextX));
      const targetTextY = minTextY + (mouseYRatio * (maxTextY - minTextY));
      
      // Convert absolute position to transform offset
      // The text element has CSS: position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%)
      // This means without any additional transform, the text is centered
      // We need to calculate how much to offset from center
      
      // Calculate where the text WOULD be if centered (its left edge position)
      const centeredTextX = (width / 2) - (textWidth / 2);
      const centeredTextY = (height / 2) - (textHeight / 2);
      
      // Calculate the offset needed from centered position
      const offsetX = targetTextX - centeredTextX;
      const offsetY = targetTextY - centeredTextY;
      
      // Apply transform with GPU acceleration (translate3d)
      textElement.style.transform = `translate(-50%, -50%) translate3d(${offsetX}px, ${offsetY}px, 0)`;

      animationFrameId.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, []);

  return (
    <div className="work-header contact-header" ref={containerRef}>
      <canvas ref={canvasRef} className="work-header-canvas" />
      <h1 ref={textRef} className="work-header-text">OPEN THE BOX</h1>
    </div>
  );
};

export default ContactHeader;

